const BASE = "/api";

async function req<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const r = await fetch(BASE + path, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
    ...init,
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

async function reqMultipart<T = unknown>(path: string, form: FormData, method: "POST" | "PATCH" = "POST"): Promise<T> {
  const r = await fetch(BASE + path, { method, credentials: "include", body: form });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error || `HTTP ${r.status}`);
  }
  return (await r.json()) as T;
}

export interface ApiUser {
  id: number;
  username: string;
  displayName: string | null;
  is_admin: number;
  created_at: string;
  last_login_at: string | null;
  categoryIds: number[];
}

export interface ApiCategory {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
}

export interface ApiVideo {
  id: number;
  title: string;
  vod_url: string;
  cover_path: string;
  description: string | null;
  created_at: string;
  category_id: number;
  category_name: string;
  category_slug: string;
  sort_order: number;
}

export const api = {
  // auth
  me: () => req<{ user: { id: number; username: string; displayName: string | null; isAdmin: boolean } | null }>("/me"),
  login: (username: string, password: string) =>
    req<{ user: { id: number; username: string; displayName: string | null; isAdmin: boolean } }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => req<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  // categories
  listCategories: () => req<{ categories: ApiCategory[] }>("/categories"),
  createCategory: (name: string, sort_order = 0, slug?: string) =>
    req<ApiCategory>("/admin/categories", {
      method: "POST",
      body: JSON.stringify({ name, sort_order, slug }),
    }),
  updateCategory: (id: number, patch: Partial<{ name: string; slug: string; sort_order: number }>) =>
    req<{ ok: boolean }>(`/admin/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteCategory: (id: number) =>
    req<{ ok: boolean }>(`/admin/categories/${id}`, { method: "DELETE" }),

  // videos
  listVideos: () => req<{ videos: ApiVideo[] }>("/videos"),
  getVideo: (id: number) => req<{ video: ApiVideo }>(`/videos/${id}`),
  createVideo: (form: FormData) => reqMultipart<{ id: number }>("/admin/videos", form),
  updateVideo: (id: number, form: FormData) =>
    reqMultipart<{ ok: boolean }>(`/admin/videos/${id}`, form, "PATCH"),
  deleteVideo: (id: number) => req<{ ok: boolean }>(`/admin/videos/${id}`, { method: "DELETE" }),
  reorderVideos: (categoryId: number, orderedIds: number[]) =>
    req<{ ok: boolean }>(`/admin/videos/reorder`, {
      method: "PUT",
      body: JSON.stringify({ categoryId, orderedIds }),
    }),
  reorderCategories: (orderedIds: number[]) =>
    req<{ ok: boolean }>(`/admin/categories/reorder`, {
      method: "PUT",
      body: JSON.stringify({ orderedIds }),
    }),

  // users
  listUsers: () => req<{ users: ApiUser[] }>("/admin/users"),
  createUser: (username: string, password: string, isAdmin = false, displayName?: string, categoryIds?: number[]) =>
    req<{ id: number; username: string; displayName: string | null; isAdmin: boolean; categoryIds: number[] }>("/admin/users", {
      method: "POST",
      body: JSON.stringify({ username, password, is_admin: isAdmin, displayName, categoryIds }),
    }),
  getUserCategories: (id: number) =>
    req<{ categoryIds: number[] }>(`/admin/users/${id}/categories`),
  setUserCategories: (id: number, categoryIds: number[]) =>
    req<{ ok: boolean; categoryIds: number[] }>(`/admin/users/${id}/categories`, {
      method: "PUT",
      body: JSON.stringify({ categoryIds }),
    }),
  updateUserDisplayName: (id: number, displayName: string | null) =>
    req<{ ok: boolean; id: number; displayName: string | null }>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ displayName }),
    }),
  resetPassword: (id: number, password: string) =>
    req<{ ok: boolean }>(`/admin/users/${id}/password`, {
      method: "PATCH",
      body: JSON.stringify({ password }),
    }),
  deleteUser: (id: number) =>
    req<{ ok: boolean }>(`/admin/users/${id}`, { method: "DELETE" }),
  bulkImportUsers: (
    users: Array<{ username: string; password: string; isAdmin: boolean; displayName?: string }>
  ) =>
    req<{
      total: number;
      ok: number;
      failed: number;
      results: Array<{ row: number; username: string; ok: boolean; error?: string }>;
    }>(`/admin/users/bulk-import`, {
      method: "POST",
      body: JSON.stringify({ users }),
    }),
  bulkDeleteUsers: (ids: number[]) =>
    req<{
      total: number;
      ok: number;
      failed: number;
      results: Array<{ id: number; username?: string; ok: boolean; error?: string }>;
    }>(`/admin/users/bulk-delete`, {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  bulkResetPassword: (ids: number[], password: string) =>
    req<{
      total: number;
      ok: number;
      failed: number;
      results: Array<{ id: number; username?: string; ok: boolean; error?: string }>;
    }>(`/admin/users/bulk-reset-password`, {
      method: "POST",
      body: JSON.stringify({ ids, password }),
    }),

  // 学习进度 + 打卡
  reportProgress: (payload: { videoId: number; position: number; duration: number; deltaSec: number }) =>
    req<{ ok: boolean; completed: boolean; justCheckedIn: boolean; watchedSec: number; durationSec: number }>(
      "/progress",
      { method: "POST", body: JSON.stringify(payload) }
    ),
  myProgress: () =>
    req<{ progress: Array<{ video_id: number; position: number; duration: number; watched_sec: number; completed: number; updated_at: string }> }>("/progress"),
  myCheckins: () =>
    req<{
      completedVideoIds: number[];
      completedCount: number;
      totalVideos: number;
      completed: Array<{ videoId: number; day: string; title: string }>;
    }>("/checkins/me"),
  adminCheckins: () =>
    req<{
      videos: Array<{ videoId: number; title: string; categoryName: string; completedCount: number }>;
      totalStudents: number;
      rows: Array<{
        id: number;
        day: string;
        userId: number;
        username: string;
        displayName: string | null;
        videoId: number;
        videoTitle: string;
      }>;
    }>("/admin/checkins"),
  adminGetSettings: () =>
    req<{ completeRatio: number; checkinEnabled: boolean }>("/admin/settings"),
  adminUpdateSettings: (patch: { completeRatio?: number; checkinEnabled?: boolean }) =>
    req<{ ok: boolean; completeRatio: number; checkinEnabled: boolean }>("/admin/settings", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  // VOD import
  vodList: (page = 1, pageSize = 50, text?: string) => {
    const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (text) q.set("text", text);
    return req<{ items: VodItem[]; total: number; page: number; pageSize: number }>(
      `/admin/vod/list?${q}`
    );
  },
  vodImport: (
    items: Array<{
      fileId: string;
      title: string;
      vod_url: string;
      cover_url: string;
      category_id: number;
      description?: string;
    }>
  ) =>
    req<{
      results: Array<{ fileId: string; ok: boolean; videoId?: number; error?: string }>;
    }>("/admin/vod/import", { method: "POST", body: JSON.stringify({ items }) }),
};

export interface VodItem {
  fileId: string;
  name: string;
  description: string;
  createTime: string;
  durationSec: number;
  sizeMB: number;
  cover: string | null;
  playUrls: { url: string; definition: number; type: string; bitrate: number }[];
  sourceUrl: string;
  imported: boolean;
}
